import { buildRecordPost } from "./recordPost.js"
import { setCollapseCommentList, initButtons, setAllContent, resetInnerHtmlForAll, initTemplateBackground } from "../helpers/collapse.js"
import { popoverInit, PATH } from "../helpers/shared-record-path.js"
import { loadEvalChart, buildTimeSeriesEvaluatedScoreChart } from "../components/chart.js"
import { requestEncode } from "../helpers/encode-record.js"
import { Audio, type RecordInfo } from "../components/audioplayer.js"
import { LIKE } from "../ui/like.js"
import { commentInit } from "../ui/comment.js"
import { RecordsSection, RecordsSectionWithBeforeList } from "../components/records-section.js"
import { getBeginRecord, getBeginUser, removePlaylistCookies, setPlaylistCookies, PlaylistManager } from "../helpers/playlist.js"
import { el, mount, setAttr, setStyle } from "redom";
import { openMainFeedOnClick } from "./mainfeedpage.js"
import { isAndroidApp, isApp } from '../../utils/app-type-checker.js'
import { getAuthUserWithHandlingError as getAuthUser, maybeMeIsMe } from '../../utils/authUser.js'
import { fetchGetJson, handleError } from "../../utils/fetch-json.js"
import { isBlocked, isBlockedUserPage } from "../helpers/report.js"
import { hostAppCall } from "../../utils/appFunctions.js"
import app from "../../utils/app.js"
import type { AudioRecord, RecordDetails } from "#/types"
import type { AppWebInterface } from "#/app.ts";

if (!window.WEBVIEW01) window.WEBVIEW01 = {}

interface FirstPlayed {
    recordId: string,
    userId: string
}
let firstPlayed: FirstPlayed
const defaultUserRelated = 2
const defaultRelatedRecordsNumber = 22
const numberOfCommentsToShow = 2

export const playRecord = (pathWithQueries: string) => recordPageInit(pathWithQueries, false, false)
export const openRecordOnClick = (pathWithQueries: string) => recordPageInit(pathWithQueries, true, true)
export const navigateToRecord = () => recordPageInit(PATH.getWindowPathWithQueries(), true, false)

Object.assign(app.featureActivations, {
    "callbackAppOnAudioPlayerStateChange": { "android": app.CHECKBUILDDATE, "iOS": app.NEVER, "buildDate": 240715 }
})

const recordPageInit = (nextPathWithQueries: string, shouldUpdateView: boolean, clicked: boolean): Promise<void> => {
    const recordAppEl = document.getElementById("record-app");
    if (recordAppEl) setStyle(recordAppEl, { display: "block" });

    const json = PATH.jsonize(nextPathWithQueries)
    Audio.recordInfo = { "id": json.id, "param": json.param }

    if (shouldUpdateView) {
        // NOTE: will add history if 1) browser's previous or next button is not pressed and 2) previous path type is not record page or it is a web version.
        // If it is clicked and the previous path type is a record page and it is an app version, then the previous path will be replaced with clicked path.
        if (clicked && (!PATH.isRecordPage() || !isApp()))
            PATH.addHistory(nextPathWithQueries)
        else
            PATH.replaceHistory(nextPathWithQueries)

        initButtons()
        initTemplateBackground()
        window.scrollTo(0, 0)
    }
    if (shouldUpdateView) {
        const allContentEl = document.getElementById("allContent");
        if (allContentEl) setStyle(allContentEl, { display: "block" });
    }
    if (!Audio.recordInfo) return Promise.resolve()
    return loadPost(Audio.recordInfo, shouldUpdateView)
}

const filterBlockUsers = (recordDetails: RecordDetails): RecordDetails => {
    recordDetails.playlist.recordsWithUser = recordDetails.playlist.recordsWithUser.filter(r => !isBlocked(r.uploader.id))
    recordDetails.relatedRecords = recordDetails.relatedRecords.filter(r => !isBlocked(r.uploader.id))
    return recordDetails
}

const getRecordUrl = (currentFile: AudioRecord): string => {
    return currentFile.cloudfrontUrl // As of May 2021. We have generous discount on Cloudfront. We redirect every traffic to there. Code below is preserved for future reference.
    // NOTE: It is known that when publicUrl is used on Apple devices it does not display the time. Further research is required.
    //    if(isAppleDevice()) return currentFile.cloudfrontUrl
    //    else return currentFile.publicUrl
}

export interface CurrentMusicInfo {
    record: {
        id: string;
        title: string;
        url: string;
        status: string;
    };
    playlistType: string;
    uploader: any;                // Replace `any` with actual uploader type if available
    param: string;
}
// TODO: combine curPost and Audio (especially, playlist) if possible. If not, remove this comment.
async function loadPost(recordInfo: RecordInfo, shouldUpdateView: boolean) {
    const maybeMe = await getAuthUser()
    const recordDetailsResult = await fetchGetJson<RecordDetails>("/v1/r/" + recordInfo.id + "/recordDetails" + recordInfo.param).catch(handleError)
    if (!recordDetailsResult) return
    const curPost = filterBlockUsers(recordDetailsResult)
    if (isBlockedUserPage(curPost.uploader.id)) return
    const playlist = new PlaylistManager(curPost.playlist)
    Audio.playlist = curPost.playlist.recordsWithUser.map(data => data.file.id) // TODO: not to use global variable. (It's from initcontent.js)

    const currentMusicInfo: CurrentMusicInfo = {
        "record": { "id": recordInfo.id, "title": curPost.record.title, "url": getRecordUrl(curPost.record), "status": curPost.record.status },
        "playlistType": curPost.playlist.playlistType,
        "uploader": curPost.uploader, "param": recordInfo.param
    }
    Audio.init(currentMusicInfo)

    document.title = currentMusicInfo.record.title + " - Soundcorset"

    if (shouldUpdateView) {
        const recordPost = buildRecordPost(curPost, numberOfCommentsToShow, maybeMe)
        // TODO: why does curPost know if the uploader has basic membership.
        const hasBasicMembership = curPost.hasBasicMembership

        Array.from(document.getElementsByClassName("popover")).forEach(el => el.remove())
        resetInnerHtmlForAll()
        setAllContent()
        document.getElementById("recordPost")?.appendChild(recordPost)

        if (curPost.record.status == "OK")
            document.getElementById("timeSeriesEvaluatedScoreChart")?.appendChild(buildTimeSeriesEvaluatedScoreChart(curPost.uploader, maybeMe, curPost.chartData, curPost.chartMetadata, curPost.record, curPost.shouldShowEvaluatedChart, hasBasicMembership))

        const myRecord = maybeMeIsMe(maybeMe) && maybeMe.get.id == curPost.uploader.id
        if (isAndroidApp() && myRecord && curPost.record.canAccessArchive && curPost.record.bitrate == 64) {
            const encodeButton = el("button#encodeTo128KButton.btn btn-lg sc_membership_button", { style: "width: 100%; margin: 0;" }, "ENCODE TO 128K")
            const encodeDiv = el("div#encodeTo128k.text-center sc_membership_div", { style: "padding: 15px 0px;" })
            mount(encodeDiv, encodeButton)
            const encodeTo128ToPasteEl = document.getElementById("encodeTo128ToPaste")
            if (encodeTo128ToPasteEl) mount(encodeTo128ToPasteEl, encodeDiv)
            if (hasBasicMembership) {
                encodeButton.onclick = () => requestEncode(curPost.record.id)
                mount(encodeDiv, el("div#encodeTo128KMessage.sc_membership_message", { style: "display: none;" }))
            } else {
                setAttr(encodeButton, { "data-trigger": "focus", "data-bs-toggle": "popover", "title": "", "data-bs-content": "<a href='/public/purchase.html?backToPrev'><b><u>Get the tools</u></b> you need to improve your skills and share your music.", "data-bs-placement": "top", "Data-original-title": "Become a member!" })
            }
        }

        LIKE.init(curPost.record.id, curPost.amILiked, curPost.record.liked, true)
        commentInit()
        popoverInit()
        setCollapseCommentList(curPost.comments.length - numberOfCommentsToShow)
    }

    if (shouldUpdateView) {
        loadEvalChart(curPost.shouldShowEvaluatedChart ? curPost.chartData : [], curPost.chartMetadata, curPost.record.length)
        if (!curPost.embed) {
            loadPlaylist(curPost, playlist)
            loadRelatedRecords(curPost, playlist)
        }
    }

    const isPlastlistTypeDefault = currentMusicInfo.playlistType == "default"
    // audioPlayer will be included after document is ready.

    // inform Android host app whenever a record is selected for playing
    if (app.isAllowedTo("callbackAppOnAudioPlayerStateChange")) {
        const hostAppArgs = {
            isPlayerRunning: true,
            recordTitle: currentMusicInfo.record.title,
            artistName: currentMusicInfo.uploader.name,
            duration: curPost.record.length,
            isDefaultPlaylist: isPlastlistTypeDefault
        }
        hostAppCall("stateChangeHandler", JSON.stringify(hostAppArgs))
    }

    Object.assign(window.WEBVIEW01, {
        audioPlayer: {
            stop: () => {
                Audio.displayPlayIcon()
                if (Audio.player != undefined) Audio.player.pause() // can't use optional chaining in iOS 12.5.5
            },
            toggle: () => { // Android only
                Audio.toggleWithoutCallback()
            },
            seekToMillis: (millis: number) => {
                if (Audio.player == undefined || !Number.isFinite(millis)) return
                Audio.player.setCurrentTime(millis / 1000)
            },
            moveToNextRecord: () => {
                if(isPlastlistTypeDefault) return
                Audio.moveToNextRecord()
            }, 
            moveToPreviousRecord: () => {
                if(isPlastlistTypeDefault) return
                Audio.moveToPrevious()
            }
        } satisfies AppWebInterface['audioPlayer']
    })
}

const loadPlaylist = (curPost: RecordDetails, playlist: PlaylistManager) => {
    if (playlist.playlistType != "default") {
        const section = new RecordsSectionWithBeforeList()
        section.sectionTitle.updateTitle("PLAYLIST")
        section.sectionTitle.addAutoplay()
        const recordsPageEl = document.getElementById("records-page")
        if (recordsPageEl) mount(recordsPageEl, section)

        const list = playlist.recordsWithUser
        const targetUrl = playlist.getTargetUrl()
        const currentNum = playlist.getCurrentIndex(curPost.record.id)
        const startIndex = Math.max(Math.min(currentNum - 1, list.length - 3), 0)
        const showMoreButton = startIndex > Math.max(list.length - 4, 0)
        section.updatePlaylist({ numberBefore: startIndex, items: list.slice(startIndex), numOfItem: 3, collapseName: "playlist", showMoreButton }, list,  { targetUrl: targetUrl ?? undefined, markedId: list[currentNum]?.file.id })
        Audio.updateImages()
    }
    // TODO: Check and remove these cookie related functions since it's hard to find when they execute. (ref: recordDetails in record/Api.scala)
    //set playlist cookies
    if (playlist.shouldRemoveCookie(curPost.record.id, curPost.uploader.id))
        removePlaylistCookies()
    if (!getBeginRecord() && !getBeginUser())
        setPlaylistCookies(curPost.record.id, curPost.uploader.id)
}

const getRandomInt = (min: number, max: number) => {
    if (max <= min) return Math.floor(Math.random() * max)
    return Math.floor(Math.random() * (max - min)) + min
}

const loadRelatedRecords = (curPost: RecordDetails, playlist: PlaylistManager) => {
    const getNextRecordWithUser = (curPost: RecordDetails) => {
        if (!firstPlayed || firstPlayed.userId != curPost.uploader.id)
            firstPlayed = { recordId: curPost.record.id, userId: curPost.uploader.id }
        // firstPlayed is used to track whether user made a full circle on uploader's records
        let potentialNextRecord = playlist.getNextRecordWithUser(curPost.record.id, curPost.uploader.id)
        // Get the next records in the user playlist
        if (!potentialNextRecord || potentialNextRecord.file.id == firstPlayed.recordId) {
            const num = getRandomInt(defaultUserRelated, curPost.relatedRecords.length)
            potentialNextRecord = curPost.relatedRecords[num]
            if (potentialNextRecord)
                Audio.playlist = [potentialNextRecord.file.id]
            // If only one record by the user, or almost made a full circle take a random record not by the user
        }
        if (!potentialNextRecord) {
            potentialNextRecord = curPost.relatedRecords[0] || { file: curPost.record, uploader: curPost.uploader }
            // Take first related if defined, record itself otherwise
            if (potentialNextRecord)
                Audio.playlist = [potentialNextRecord.file.id]
        }
        return potentialNextRecord
    }
    const relatedRecordSection = new RecordsSection()
    const shouldShowUpNext = playlist.playlistType == "default"
    mount(relatedRecordSection, el("button.showMore clickable btn btn-light", { onclick: openMainFeedOnClick, style: "margin: 0 0 40px 0", innerText: "SHOW MORE" }))
    const recordsPageEl = document.getElementById("records-page")
    if (!recordsPageEl) return;
    if (shouldShowUpNext) {
        const upNext = new RecordsSection()
        const nextRecord = getNextRecordWithUser(curPost)
        upNext.init({ items: [{ file: nextRecord.file, uploader: nextRecord.uploader }], numOfItem: 1 }, { isUpNext: true })
        relatedRecordSection.init({ items: curPost.relatedRecords.filter(item => item.file.id != nextRecord.file.id), numOfItem: defaultRelatedRecordsNumber })
        relatedRecordSection.sectionTitle.updateTitle("RELATED RECORDS")
        upNext.sectionTitle.updateTitle("UP NEXT")
        upNext.sectionTitle.addAutoplay()
        mount(recordsPageEl, upNext)
    }
    else {
        relatedRecordSection.init({ numOfItem: defaultRelatedRecordsNumber, items: curPost.relatedRecords })
        relatedRecordSection.sectionTitle.updateTitle("RELATED RECORDS")
    }
    mount(recordsPageEl, relatedRecordSection)
}

window.addEventListener("popstate", () => PATH.getCurrentPathType().init())